import { chartColors } from '@scality/core-ui/dist/style/theme';
import { useCallback, useMemo, useRef, useState } from 'react';

/**
 * core-ui's chart palette, in core-ui's order, without the red.
 *
 * `lineColor6` (#BE2543) sits next to the theme's `statusCritical`: a whole box filled with it
 * reads as an alarm whatever it holds, where a line of eight in that red reads as one series.
 */
const PROPORTION_BOX_COLORS = Object.entries(chartColors)
  .filter(([name]) => name !== 'lineColor6')
  .map(([, color]) => color);

/** The colour of each box, by node key, handed out in the order the boxes are met. */
export const useColorByNodeKey = (keys: string[]): Record<string, string> =>
  useMemo(
    () =>
      Object.fromEntries(keys.map((key, index) => [key, PROPORTION_BOX_COLORS[index % PROPORTION_BOX_COLORS.length]])),
    [keys],
  );

/** The width of the element the returned ref lands on, kept current as it resizes; null until measured. */
export const useObservedWidthPx = () => {
  const [widthPx, setWidthPx] = useState<number | null>(null);
  const observerRef = useRef<ResizeObserver | null>(null);

  const observeWidth = useCallback((element: HTMLDivElement | null) => {
    observerRef.current?.disconnect();
    if (!element) return;
    const observer = new ResizeObserver(([entry]) => setWidthPx(entry.contentRect.width));
    observer.observe(element);
    observerRef.current = observer;
  }, []);

  return [observeWidth, widthPx] as const;
};
